import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { Mail, MapPin, Facebook, Twitter, Linkedin, Instagram } from 'lucide-react';

const Footer = () => {
  const { language } = useLanguage();

  const t = {
    fr: {
      discover: 'Découvrir',
      ourPosture: 'Notre posture',
      proposals: 'Propositions',
      resources: 'Ressources',
      terrain: 'Terrain',
      organization: 'Organisation',
      governance: 'Gouvernance',
      join: 'Rejoindre',
      description: 'Alliance Démocratique Africaine. Parti politique autorisé depuis septembre 2024.',
      allRights: 'Tous droits réservés.'
    },
    en: {
      discover: 'Discover',
      ourPosture: 'Our posture',
      proposals: 'Proposals',
      resources: 'Resources',
      terrain: 'Field',
      organization: 'Organization',
      governance: 'Governance',
      join: 'Join',
      description: 'African Democratic Alliance. Authorized political party since September 2024.',
      allRights: 'All rights reserved.'
    },
    pt: {
      discover: 'Descobrir',
      ourPosture: 'A nossa postura',
      proposals: 'Propostas',
      resources: 'Recursos',
      terrain: 'Terreno',
      organization: 'Organização',
      governance: 'Governança',
      join: 'Aderir',
      description: 'Aliança Democrática Africana. Partido político autorizado desde setembro de 2024.',
      allRights: 'Todos os direitos reservados.'
    }
  };

  const content = t[language] || t.fr;

  const socialLinks = [
    { icon: Facebook, href: '#', label: 'Facebook' },
    { icon: Twitter, href: '#', label: 'Twitter' },
    { icon: Linkedin, href: '#', label: 'LinkedIn' },
    { icon: Instagram, href: '#', label: 'Instagram' },
  ];

  return (
    <footer className="bg-[#1A0F2E] text-white">
      <div className="container mx-auto px-4 lg:px-10 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          
          {/* Brand */}
          <div>
            <Link to="/" className="flex items-center gap-3 mb-4">
              <img src="/logo-ada.png" alt="ADA" className="h-10 w-auto" />
              <div>
                <span className="text-lg font-bold">ADA</span>
                <span className="block text-[10px] text-white/40 tracking-widest uppercase">Côte d'Ivoire</span>
              </div>
            </Link>
            <p className="text-white/50 text-sm mb-4">{content.description}</p>
            <div className="space-y-2 text-sm text-white/50">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-[#F7C948]" />
                <span>contact@ada.org</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-[#F7C948]" />
                <span>Abidjan, Côte d'Ivoire</span>
              </div>
            </div>
          </div>

          {/* Links */}
          <div>
            <h4 className="text-xs font-bold tracking-widest uppercase text-white/30 mb-4">{content.discover}</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/a-propos" className="text-white/60 hover:text-white">ADA</Link></li>
              <li><Link to="/notre-posture" className="text-white/60 hover:text-white">{content.ourPosture}</Link></li>
              <li><Link to="/propositions" className="text-white/60 hover:text-white">{content.proposals}</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="text-xs font-bold tracking-widest uppercase text-white/30 mb-4">{content.resources}</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/tribunes" className="text-white/60 hover:text-white">Tribunes</Link></li>
              <li><Link to="/terrain" className="text-white/60 hover:text-white">{content.terrain}</Link></li>
              <li><Link to="/scorecard" className="text-white/60 hover:text-white">Scorecard</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="text-xs font-bold tracking-widest uppercase text-white/30 mb-4">{content.organization}</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/organigramme" className="text-white/60 hover:text-white">{language === 'fr' ? 'Organigramme' : language === 'en' ? 'Organization' : 'Organigrama'}</Link></li>
              <li><Link to="/gouvernance" className="text-white/60 hover:text-white">{content.governance}</Link></li>
              <li><Link to="/rejoindre" className="text-white/60 hover:text-white">{content.join}</Link></li>
              <li><Link to="/don" className="text-[#0D9488] hover:text-[#14b8a6] font-medium">{language === 'fr' ? 'Faire un don' : language === 'en' ? 'Donate' : 'Doar'}</Link></li>
            </ul>
            <div className="flex items-center gap-3 mt-6">
              {socialLinks.map((social) => {
                const Icon = social.icon;
                return (
                  <a
                    key={social.label}
                    href={social.href}
                    className="w-8 h-8 rounded bg-white/5 flex items-center justify-center hover:bg-white/10"
                    aria-label={social.label}
                  >
                    <Icon className="w-4 h-4 text-white/60" />
                  </a>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom */}
      <div className="border-t border-white/10">
        <div className="container mx-auto px-4 lg:px-10 py-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-white/40">
            <span>© {new Date().getFullYear()} ADA · Alliance Démocratique Africaine. {content.allRights}</span>
            <div className="flex items-center gap-4">
              <Link to="/mentions-legales" className="hover:text-white/60">{language === 'fr' ? 'Mentions légales' : language === 'en' ? 'Legal notice' : 'Avisos legais'}</Link>
              <span className="text-white/30 text-[10px]">Made by ADA · powered by Emergent</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
